import React, { useState, useEffect } from 'react';
import {
  FileText, Save, RefreshCw, CheckCircle, AlertCircle, Settings,
  PenTool, Key, Link, TestTube, Download, Send, Eye, Edit3, X
} from 'lucide-react';

// Modèle de contrat par défaut
const DEFAULT_CONTRACT_TEMPLATE = `CONTRAT D'AUTORISATION DE TEST D'INTRUSION ET D'ATTAQUE SUR RÉSEAU INFORMATIQUE

(avec information et acceptation des risques)

Entre les soussignés :

[Nom du Client],
[forme juridique], au capital de [X] EUR,
dont le siège social est situé [adresse complète],
immatriculée au RCS de [ville] sous le numéro [X],
représentée par [Nom, fonction], dûment habilité,
Ci-après dénommée "le Client",

D'UNE PART,

ET

[Nom du Prestataire],
[forme juridique], au capital de [X] EUR,
dont le siège social est situé [adresse complète],
immatriculée au RCS de [ville] sous le numéro [X],
représentée par [Nom, fonction], dûment habilité,
Ci-après dénommée "le Prestataire",

D'AUTRE PART,

Ensemble dénommés "les Parties" ou individuellement une "Partie".

---

1. OBJET DU CONTRAT

Le présent contrat a pour objet de définir les conditions dans lesquelles le Prestataire est autorisé par le Client à réaliser des tests d'intrusion, simulations d'attaques et autres actions offensives de cybersécurité (ci-après les "Tests") sur les systèmes d'information du Client, dans le but d'identifier des failles de sécurité et de formuler des recommandations.

Les Tests peuvent inclure, selon ce qui est défini dans ce contrat et ses annexes :
- Tests boîte noire, boîte grise et/ou boîte blanche ;
- Tests sur réseau interne, réseau externe, applications Web, services exposés, systèmes industriels/OT, équipements réseau, Wi-Fi, etc. ;
- Simulations d'attaques techniques (exploitation de vulnérabilités, élévation de privilèges, mouvements latéraux, etc.) dans les limites définies au présent contrat.

---

2. CHAMP D'APPLICATION ET PÉRIMÈTRE

Les Tests sont strictement limités au périmètre suivant, fourni par le Client et accepté par le Prestataire :

Plages d'adresses IP / noms de domaine :
[Liste des IP, sous-réseaux, VLAN, FQDN, etc.]

Applications / services concernés :
[Liste des applications, portails, API, etc.]

Systèmes / environnements inclus :
[Production / Préproduction / Recette / Développement - préciser]

Éventuels systèmes industriels / OT :
[Ex : automates, SCADA, protocoles industriels (Modbus, DNP3, IEC-104, OPC UA, Profinet, etc.) - préciser très clairement les limites]

Tout système non explicitement mentionné dans ce périmètre est exclu des Tests.
Toute extension de périmètre devra faire l'objet d'un avenant écrit accepté par les deux Parties.

---

3. PÉRIODE ET CRÉNEAUX D'INTERVENTION

Les Tests seront réalisés entre les dates suivantes :
- Date de début des Tests : [JJ/MM/AAAA]
- Date de fin des Tests : [JJ/MM/AAAA]

Créneaux autorisés (horaires locales du Client) :
[Par exemple : du lundi au vendredi, de 20h00 à 06h00, heures non ouvrées, afin de limiter l'impact sur la production.]

Toute modification de ces créneaux devra être validée par écrit (email ou autre moyen convenu) par les deux Parties.

---

4. INFORMATION SUR LES RISQUES ET ACCEPTATION

Le Client reconnaît avoir été informé par le Prestataire que, malgré toutes les précautions prises, les Tests peuvent entraîner notamment, sans que cette liste soit exhaustive :

1. Ralentissements, dégradations de performance ou indisponibilités temporaires de certains services ;
2. Blocages d'applications ou de sessions utilisateurs ;
3. Faux positifs de sécurité (déclenchements d'alertes sur des outils de supervision / sécurité) ;
4. Corruption ou perte de données en cas de combinatoire imprévue de vulnérabilités ou dysfonctionnement de systèmes ;
5. Risques accrus sur des systèmes sensibles, notamment :
   - Infrastructures industrielles/OT (automates, SCADA, protocoles temps-réel) pouvant impacter la production ;
   - Équipements réseau critiques (firewall, routeurs cœur de réseau, équipements opérateurs, etc.) ;
   - Systèmes anciens / obsolètes (systèmes non maintenus, OS EOL, équipements non redondés, etc.).

Le Prestataire s'engage à :
- Mettre en œuvre les bonnes pratiques du métier pour limiter les risques (tests progressifs, intensité contrôlée, pauses si comportement anormal, etc.) ;
- Arrêter immédiatement un test en cas de dysfonctionnement grave constaté ou signalé par le Client ;
- Informer le Client dès la découverte d'un risque sérieux pouvant entraîner un impact significatif.

Le Client :
- Accepte explicitement ces risques liés à la réalisation de Tests d'intrusion ;
- Reconnaît être responsable de la mise en place de sauvegardes récentes, vérifiées et restaurables avant les Tests ;
- S'engage à désigner un contact technique joignable pendant toute la durée des Tests afin de pouvoir interrompre ou adapter ceux-ci en cas de problème.

---

5. AUTORISATION EXPRESSE DU CLIENT

Le Client autorise expressément le Prestataire à :
1. Réaliser des Tests d'intrusion et simulations d'attaque sur les systèmes définis au périmètre ;
2. Se connecter, scanner, analyser, tenter d'exploiter des vulnérabilités et évaluer le niveau de sécurité des systèmes du Client dans ce périmètre ;
3. Utiliser des outils de pentest, d'exploitation, d'analyse de trafic, de fuzzing, etc., dans les limites des bonnes pratiques et du périmètre autorisé.

Cette autorisation est limitée :
- Dans le temps : aux dates / créneaux définis à l'article 3 ;
- Dans le périmètre : aux systèmes explicitement listés à l'article 2 ;
- Dans le but : exclusivement pour une mission d'audit de sécurité et non pour causer volontairement un dommage.

Toute action en dehors de ces limites devra être préalablement autorisée par écrit par le Client.

---

6. CONFIDENTIALITÉ ET TRAITEMENT DES DONNÉES

Le Prestataire s'engage à :
1. Traiter comme strictement confidentielles toutes les informations, données et vulnérabilités auxquelles il aurait accès ;
2. Utiliser les données collectées uniquement pour la réalisation de la mission et l'élaboration des livrables ;
3. Ne pas transmettre à des tiers les informations relatives au système d'information du Client sans l'accord écrit de ce dernier ;
4. Mettre en place des mesures techniques et organisationnelles adaptées pour protéger les informations collectées (chiffrement, contrôle d'accès, durées de conservation limitées, etc.).

Le Client s'engage à :
- Ne pas diffuser le contenu du rapport de Tests à des tiers sans anonymiser préalablement les éléments sensibles, sauf accord express du Prestataire pour les éléments le concernant.

---

7. RESPONSABILITÉS

Le Prestataire :
- Est tenu à une obligation de moyens et non de résultat ;
- S'engage à exécuter les Tests conformément aux règles de l'art, aux standards du métier (par ex. OWASP, guides ANSSI, etc.) et au périmètre défini ;
- Ne pourra être tenu responsable de dommages résultant :
  - De l'absence ou de l'insuffisance de sauvegardes du Client ;
  - De dysfonctionnements préexistants dans l'infrastructure du Client ;
  - De l'utilisation des résultats de Tests par le Client ou par des tiers à d'autres fins que la sécurisation du système.

Le Client :
- Est responsable de la validité de l'autorisation qu'il donne (propriétaire ou gestionnaire légitime des systèmes testés) ;
- S'engage à fournir des informations exactes sur l'infrastructure et ses contraintes (systèmes critiques, OT, services vitaux, etc.) ;
- Est responsable des décisions de correction, remédiation, durcissement qui seront prises suite aux recommandations du Prestataire.

---

8. LIVRABLES

À l'issue des Tests, le Prestataire fournira au Client :

1. Un rapport technique détaillé, comprenant notamment :
   - Le périmètre et la méthodologie de Tests ;
   - La liste des vulnérabilités identifiées, leur criticité et leur preuve de concept ;
   - Les vecteurs d'attaque, chemins de compromission, impacts potentiels ;
   - Les spécificités éventuelles liées aux systèmes industriels / OT.

2. Un rapport de synthèse à destination de la Direction, présentant :
   - Les principaux risques identifiés ;
   - Une priorisation des actions de remédiation ;
   - Des recommandations stratégiques pour renforcer la sécurité globale du SI.

Les livrables sont remis sous format [PDF / DOCX / autre] et peuvent, si convenu, faire l'objet d'une restitution orale.

---

9. DURÉE DU CONTRAT

Le présent contrat entre en vigueur à compter de sa date de signature par les deux Parties et reste valable jusqu'à :
- La remise des livrables finaux au Client,
OU
- La date du [JJ/MM/AAAA], si elle est antérieure.

Toute nouvelle campagne de Tests en dehors de cette période nécessitera la signature d'un nouveau contrat ou d'un avenant.

---

10. RÉSILIATION ANTICIPÉE

Chaque Partie pourra résilier le contrat en cas de manquement grave de l'autre Partie à ses obligations, non réparé dans un délai de [30] jours à compter de la notification écrite du manquement.

En cas de résiliation à l'initiative du Client alors que les Tests sont déjà engagés, le Prestataire pourra facturer les prestations réalisées au prorata, ainsi que les frais engagés et non récupérables.

---

11. DROIT APPLICABLE ET JURIDICTION COMPÉTENTE

Le présent contrat est régi par le droit français.

En cas de litige relatif à sa validité, son interprétation ou son exécution, et à défaut de résolution amiable, les Parties attribuent compétence exclusive aux tribunaux de [Ville], nonobstant pluralité de défendeurs ou appel en garantie.

---

12. SIGNATURES ET CLAUSE D'ACCEPTATION DES RISQUES

En signant le présent contrat, le Client :
- Reconnaît avoir été informé de la nature offensive des Tests ;
- Comprend et accepte les risques inhérents aux Tests décrits à l'article 4 ;
- Autorise expressément le Prestataire à réaliser les Tests conformément aux termes du présent contrat.

Fait à [Ville], le [JJ/MM/AAAA], en deux exemplaires originaux.


Pour le Client
Nom :
Fonction :
Signature précédée de la mention "Lu et approuvé, bon pour accord et acceptation des risques de Tests d'intrusion" :

[Signature]


Pour le Prestataire
Nom :
Fonction :
Signature précédée de la mention "Lu et approuvé" :

[Signature]
`;

// Fournisseurs de signature electronique
const SIGNATURE_PROVIDERS = [
  {
    id: 'docusign',
    name: 'DocuSign',
    logo: 'https://www.docusign.com/favicon.ico',
    description: 'Leader mondial de la signature electronique',
    color: '#FFD700',
    fields: [
      { key: 'api_key', label: 'API Key (Integration Key)', type: 'password', required: true },
      { key: 'account_id', label: 'Account ID', type: 'text', required: true },
      { key: 'base_url', label: 'Base URL', type: 'text', required: true, placeholder: 'https://demo.docusign.net/restapi' },
      { key: 'user_id', label: 'User ID (API Username)', type: 'text', required: true }
    ]
  },
  {
    id: 'adobe_sign',
    name: 'Adobe Sign',
    logo: 'https://www.adobe.com/favicon.ico',
    description: 'Solution Adobe Acrobat Sign',
    color: '#FF0000',
    fields: [
      { key: 'client_id', label: 'Client ID', type: 'text', required: true },
      { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
      { key: 'refresh_token', label: 'Refresh Token', type: 'password', required: true },
      { key: 'base_url', label: 'API Base URL', type: 'text', required: true, placeholder: 'https://api.eu1.adobesign.com' }
    ]
  },
  {
    id: 'hellosign',
    name: 'HelloSign (Dropbox Sign)',
    logo: 'https://www.hellosign.com/favicon.ico',
    description: 'Solution Dropbox Sign',
    color: '#00B4E6',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', required: true },
      { key: 'client_id', label: 'Client ID', type: 'text', required: false },
      { key: 'test_mode', label: 'Mode Test', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'oodrive_sign',
    name: 'Oodrive Sign',
    logo: 'https://www.oodrive.com/favicon.ico',
    description: 'Solution francaise de signature qualifiee eIDAS',
    color: '#E94E1B',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', required: true },
      { key: 'api_secret', label: 'API Secret', type: 'password', required: true },
      { key: 'environment', label: 'Environnement', type: 'select', options: ['production', 'sandbox'], required: true },
      { key: 'organization_id', label: 'Organization ID', type: 'text', required: true }
    ]
  },
  {
    id: 'odoo_sign',
    name: 'Odoo Sign',
    logo: 'https://www.odoo.com/favicon.ico',
    description: 'Module signature integre a Odoo ERP',
    color: '#714B67',
    fields: [
      { key: 'odoo_url', label: 'URL Odoo', type: 'text', required: true, placeholder: 'https://votre-instance.odoo.com' },
      { key: 'database', label: 'Database', type: 'text', required: true },
      { key: 'api_key', label: 'API Key', type: 'password', required: true },
      { key: 'user_id', label: 'User ID', type: 'text', required: true }
    ]
  }
];

const ContractManager = () => {
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerConfig, setProviderConfig] = useState({});
  const [configSaved, setConfigSaved] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  const [contractTemplate, setContractTemplate] = useState(DEFAULT_CONTRACT_TEMPLATE);
  const [editingContract, setEditingContract] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Charger la configuration sauvegardee
  useEffect(() => {
    const savedConfig = localStorage.getItem('signature_provider_config');
    if (savedConfig) {
      const config = JSON.parse(savedConfig);
      setSelectedProvider(config.provider);
      setProviderConfig(config.fields || {});
      setConfigSaved(true);
    }

    const savedContract = localStorage.getItem('contract_template');
    if (savedContract) {
      setContractTemplate(savedContract);
    }
  }, []);

  const handleProviderSelect = (providerId) => {
    setSelectedProvider(providerId);
    setProviderConfig({});
    setConfigSaved(false);
    setConnectionStatus(null);
  };

  const handleConfigChange = (key, value) => {
    setProviderConfig(prev => ({
      ...prev,
      [key]: value
    }));
    setConfigSaved(false);
  };

  const saveProviderConfig = () => {
    const config = {
      provider: selectedProvider,
      fields: providerConfig,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem('signature_provider_config', JSON.stringify(config));
    setConfigSaved(true);
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);

    // Simulation de test de connexion
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Pour la demo, on simule un succes si tous les champs requis sont remplis
    const provider = SIGNATURE_PROVIDERS.find(p => p.id === selectedProvider);
    const allRequiredFilled = provider?.fields
      .filter(f => f.required)
      .every(f => providerConfig[f.key]);

    setConnectionStatus(allRequiredFilled ? 'success' : 'error');
    setTestingConnection(false);
  };

  const saveContract = () => {
    setSaving(true);
    localStorage.setItem('contract_template', contractTemplate);
    setTimeout(() => {
      setSaving(false);
      setEditingContract(false);
    }, 500);
  };

  const resetContract = () => {
    if (window.confirm('Voulez-vous vraiment réinitialiser le contrat au modèle par défaut ?')) {
      setContractTemplate(DEFAULT_CONTRACT_TEMPLATE);
      localStorage.setItem('contract_template', DEFAULT_CONTRACT_TEMPLATE);
    }
  };

  const downloadContract = () => {
    const blob = new Blob([contractTemplate], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contrat_autorisation_pentest.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const currentProvider = SIGNATURE_PROVIDERS.find(p => p.id === selectedProvider);

  return (
    <div className="space-y-6">
      {/* Section API Signature Electronique */}
      <div className="bg-brand-black rounded-lg p-5 border border-gray-800">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <PenTool className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="font-bold text-white">API de Signature Électronique</h3>
            <p className="text-xs text-gray-400">Configurez votre fournisseur de signature électronique pour les contrats</p>
          </div>
        </div>

        {/* Selection du fournisseur */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {SIGNATURE_PROVIDERS.map((provider) => (
            <button
              key={provider.id}
              onClick={() => handleProviderSelect(provider.id)}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedProvider === provider.id
                  ? 'border-brand-green bg-brand-green/10'
                  : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg"
                  style={{ backgroundColor: provider.color }}
                >
                  {provider.name.charAt(0)}
                </div>
                <span className="text-sm font-medium text-white text-center">{provider.name}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Configuration du fournisseur selectionne */}
        {currentProvider && (
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: currentProvider.color }}
                >
                  {currentProvider.name.charAt(0)}
                </div>
                <div>
                  <h4 className="font-medium text-white">{currentProvider.name}</h4>
                  <p className="text-xs text-gray-400">{currentProvider.description}</p>
                </div>
              </div>
              {configSaved && (
                <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Configure
                </span>
              )}
            </div>

            <div className="grid gap-4">
              {currentProvider.fields.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  {field.type === 'select' ? (
                    <select
                      value={providerConfig[field.key] || ''}
                      onChange={(e) => handleConfigChange(field.key, e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-brand-green focus:outline-none"
                    >
                      <option value="">Selectionner...</option>
                      {field.options.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : field.type === 'checkbox' ? (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={providerConfig[field.key] || false}
                        onChange={(e) => handleConfigChange(field.key, e.target.checked)}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-brand-green focus:ring-brand-green"
                      />
                      <span className="text-sm text-gray-400">Activer</span>
                    </label>
                  ) : (
                    <input
                      type={field.type}
                      value={providerConfig[field.key] || ''}
                      onChange={(e) => handleConfigChange(field.key, e.target.value)}
                      placeholder={field.placeholder || ''}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-brand-green focus:outline-none"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-700">
              <button
                onClick={saveProviderConfig}
                className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-green-600 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Sauvegarder
              </button>
              <button
                onClick={testConnection}
                disabled={testingConnection}
                className="px-4 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/50 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {testingConnection ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Test en cours...
                  </>
                ) : (
                  <>
                    <TestTube className="w-4 h-4" />
                    Tester la connexion
                  </>
                )}
              </button>
              {connectionStatus === 'success' && (
                <span className="text-green-400 text-sm flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Connexion réussie
                </span>
              )}
              {connectionStatus === 'error' && (
                <span className="text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Échec de connexion
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Section Contrat Type */}
      <div className="bg-brand-black rounded-lg p-5 border border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <FileText className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="font-bold text-white">Contrat Type d'Autorisation de Pentest</h3>
              <p className="text-xs text-gray-400">Modèle de contrat modifiable pour les missions de test d'intrusion</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className={`px-3 py-2 rounded-lg transition-colors text-sm flex items-center gap-2 ${
                previewMode
                  ? 'bg-brand-green text-brand-black'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <Eye className="w-4 h-4" />
              {previewMode ? 'Mode Édition' : 'Aperçu'}
            </button>
            <button
              onClick={downloadContract}
              className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Telecharger
            </button>
            <button
              onClick={resetContract}
              className="px-3 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-colors text-sm flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Réinitialiser
            </button>
          </div>
        </div>

        {/* Zone de contrat */}
        <div className="relative">
          {previewMode ? (
            <div className="bg-white text-gray-900 rounded-lg p-8 max-h-[600px] overflow-y-auto font-serif">
              <pre className="whitespace-pre-wrap font-serif text-sm leading-relaxed">
                {contractTemplate}
              </pre>
            </div>
          ) : (
            <textarea
              value={contractTemplate}
              onChange={(e) => setContractTemplate(e.target.value)}
              className="w-full h-[600px] bg-gray-900 border border-gray-700 rounded-lg p-4 text-gray-300 font-mono text-sm focus:border-brand-green focus:outline-none resize-none"
              placeholder="Contenu du contrat..."
            />
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
          <div className="text-xs text-gray-500">
            {contractTemplate.length} caracteres | {contractTemplate.split('\n').length} lignes
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={saveContract}
              disabled={saving}
              className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-green-600 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Sauvegarder le Contrat
                </>
              )}
            </button>
            {selectedProvider && configSaved && (
              <button
                className="px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/50 rounded-lg hover:bg-purple-500/30 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Envoyer pour signature
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Info section */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-blue-500 mt-0.5">&#9432;</div>
          <div className="text-sm text-gray-300">
            <div className="font-medium text-blue-400 mb-2">A propos du contrat d'autorisation</div>
            <ul className="space-y-1 text-xs text-gray-400">
              <li><strong className="text-yellow-400">Obligatoire</strong>: Ce contrat doit etre signe AVANT tout test d'intrusion</li>
              <li><strong className="text-green-400">Perimetre</strong>: Definissez precisement les systemes autorises (IP, domaines, applications)</li>
              <li><strong className="text-orange-400">Risques</strong>: Le client doit etre informe et accepter les risques lies aux tests</li>
              <li><strong className="text-purple-400">Signature</strong>: Utilisez une solution de signature electronique pour la validite juridique</li>
              <li><strong className="text-red-400">Conservation</strong>: Conservez ce contrat signe pendant toute la duree legale (5 ans minimum)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractManager;
